<script lang="ts">
  import {
    agendarCita, cuposDia, hoyIso, isoLocalDateTime,
    mananaIso, pasadoMananaIso, geminiChat,
    type ClinicaPublicaInfo, type CuposDia,
  } from '../lib/citas-publico';
  import { rubroFromTipo } from '../lib/citas-rubro';

  interface Props { tenantId: string; clinica: ClinicaPublicaInfo; }
  let { tenantId, clinica }: Props = $props();

  type Msg = { who: 'bot' | 'user' | 'system'; text: string; time?: string; id: number };
  let msgs = $state<Msg[]>([]);
  let msgId = 0;
  let input = $state('');
  let enviando = $state(false);
  let pensando = $state(false);
  let error = $state<string | null>(null);
  let escuchando = $state(false);
  let vozActiva = $state(true);
  let citaAgendada = $state(false);

  let nombre = $state('');
  let telefono = $state('');
  let dia = $state('');
  let hora = $state('');
  let motivo = $state('');
  let cupos = $state<CuposDia | null>(null);

  const primary = $derived(clinica.branding?.primaryColor ?? '#15803d');
  const rubro = $derived(rubroFromTipo(clinica.tipoOrganizacion));
  const SpeechRecognition = typeof window !== 'undefined' ? (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition : null;
  const vozOK = typeof window !== 'undefined' && !!SpeechRecognition;

  function horaMsg() { return new Date().toLocaleTimeString('es-GT', { hour:'2-digit', minute:'2-digit' }); }

  function addMsg(who: Msg['who'], text: string) {
    msgs = [...msgs, { who, text, time: horaMsg(), id: ++msgId }];
    if (who === 'bot' && vozActiva && 'speechSynthesis' in window) {
      speechSynthesis.cancel();
      speechSynthesis.speak(new SpeechSynthesisUtterance(text.replace(/<[^>]+>/g, '')));
    }
  }

  function iniciar() {
    msgs = []; citaAgendada = false; pensando = false; enviando = false;
    nombre = telefono = dia = hora = motivo = ''; cupos = null; error = null;
    addMsg('bot', `¡Hola! Soy Gigi ✨, asistente de <b>${clinica.nombre}</b>. ${rubro.saludo} Estoy aquí para ayudarle a agendar su cita. ¿Cuál es su nombre completo?`);
  }

  let initKey = $state('');
  $effect(() => {
    const key = `${tenantId}|${clinica.tenantId ?? ''}|${clinica.nombre}|${clinica.tipoOrganizacion ?? ''}`;
    if (initKey === key) return;
    initKey = key; iniciar();
  });

  async function processMessage(txt: string) {
    if (!txt.trim() || enviando) return;
    enviando = true; pensando = true; error = null;
    try {
      const history = msgs.filter(m => m.who !== 'system').map(m => ({ who: m.who, text: m.text }));
      const r = await geminiChat(clinica.tenantId, txt, history);
      pensando = false; addMsg('bot', r.reply);
      if (r.data?.nombre && r.data?.telefono && r.data?.fecha && r.data?.hora) {
        nombre = r.data.nombre; telefono = r.data.telefono;
        dia = r.data.fecha; hora = r.data.hora; motivo = r.data.motivo || '';
        setTimeout(() => confirmarCita(), 800);
      }
    } catch {
      pensando = false; addMsg('bot', 'Disculpe, tuve un problema de conexión. ¿Podría intentarlo de nuevo?');
    } finally { enviando = false; }
  }

  function enviarMsg() {
    const txt = input.trim(); if (!txt) return;
    input = ''; addMsg('user', txt); processMessage(txt);
  }

  function quickReply(value: string) {
    addMsg('user', value); processMessage(value);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarMsg(); }
  }

  async function confirmarCita() {
    pensando = true;
    try {
      const r = await agendarCita(tenantId, { pacienteNombre: nombre, telefono, fechaHora: isoLocalDateTime(dia, hora), motivo: motivo || undefined });
      pensando = false; citaAgendada = true;
      addMsg('system', `✅ Cita agendada<br><b>${nombre}</b><br>📅 ${dia} a las ${hora}<br>📞 ${telefono}${motivo ? '<br>📝 ' + motivo : ''}<br><small>Recibirá recordatorio por WhatsApp</small>`);
    } catch (e: any) {
      pensando = false; error = e?.message ?? 'Error al agendar';
      addMsg('bot', `❌ No se pudo agendar: ${error}. Intentemos de nuevo.`);
    }
  }

  function toggleMic() {
    if (!SpeechRecognition) return;
    if (escuchando) { escuchando = false; return; }
    const rec = new SpeechRecognition();
    rec.lang = 'es-GT'; rec.interimResults = false;
    rec.onresult = (ev: any) => {
      const txt = ev.results[0][0].transcript;
      escuchando = false;
      addMsg('user', txt); processMessage(txt);
    };
    rec.onerror = () => { escuchando = false; };
    rec.onend = () => { escuchando = false; };
    escuchando = true; rec.start();
  }
</script>

<div class="gigi-chat" style="--gigi: {primary}">
  <div class="gigi-header">
    <div class="gigi-avatar">
      {#if clinica.branding?.logoUrl}
        <img src={clinica.branding.logoUrl} alt="" />
      {:else}
        <span>✨</span>
      {/if}
    </div>
    <div class="gigi-info">
      <div class="gigi-name">Gigi · Asistente IA</div>
      <div class="gigi-clinic">{clinica.nombre} · {rubro.label}</div>
    </div>
    <div class="gigi-tools">
      <button class="gigi-tool" class:on={vozActiva} onclick={() => vozActiva = !vozActiva} title="Voz">🔊</button>
      {#if vozOK}
        <button class="gigi-tool mic" class:on={escuchando} onclick={toggleMic} title="Micrófono">🎤</button>
      {/if}
    </div>
  </div>

  <div class="gigi-msgs" id="gigi-scroll">
    {#each msgs as m (m.id)}
      {#if m.who === 'system'}
        <div class="gigi-card">
          {@html m.text}
          <div class="gigi-card-actions">
            <button onclick={iniciar}>Agendar otra cita</button>
          </div>
        </div>
      {:else}
        <div class="gigi-msg" class:bot={m.who === 'bot'} class:user={m.who === 'user'}>
          <div class="gigi-bubble">{@html m.text}</div>
          <span class="gigi-time">{m.time}</span>
        </div>
      {/if}
    {/each}
    {#if pensando}
      <div class="gigi-msg bot">
        <div class="gigi-bubble thinking">
          <span class="dot"></span><span class="dot"></span><span class="dot"></span>
        </div>
      </div>
    {/if}
  </div>

  {#if !citaAgendada && !pensando}
    <div class="gigi-chips">
      <button onclick={() => quickReply('Quiero agendar una cita para hoy')}>📅 Hoy</button>
      <button onclick={() => quickReply('Quiero agendar para mañana')}>📅 Mañana</button>
      <button onclick={() => quickReply('Hola, necesito una consulta médica')}>🩺 Consulta</button>
      <button onclick={() => quickReply('¿Qué servicios ofrecen?')}>ℹ️ Info</button>
    </div>
  {/if}

  {#if !citaAgendada}
    <div class="gigi-input-bar">
      <input bind:value={input} onkeydown={onKey} placeholder={escuchando ? '🎤 Escuchando...' : 'Escriba su mensaje...'} disabled={enviando} />
      <button class="gigi-send" onclick={enviarMsg} disabled={enviando || !input.trim()} title="Enviar">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
      </button>
    </div>
  {:else}
    <div class="gigi-input-bar done">
      <button onclick={iniciar}>🔄 Agendar otra cita</button>
    </div>
  {/if}

  {#if error}<div class="gigi-error">{error}</div>{/if}
</div>

<style>
  .gigi-chat { display:flex;flex-direction:column;height:100%;max-height:600px;min-height:420px;
    background:rgba(255,255,255,.85);backdrop-filter:blur(30px);-webkit-backdrop-filter:blur(30px);
    border:1px solid rgba(255,255,255,.5);border-radius:20px;overflow:hidden;
    box-shadow:0 16px 48px rgba(0,0,0,.08);font-family:Inter,system-ui,sans-serif; }
  .gigi-header { display:flex;align-items:center;gap:10px;padding:12px 16px;
    background:linear-gradient(135deg,var(--gigi),color-mix(in srgb,var(--gigi) 85%,#000));color:#fff; }
  .gigi-avatar { width:40px;height:40px;border-radius:12px;background:rgba(255,255,255,.2);
    display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;
    border:1px solid rgba(255,255,255,.3);font-size:1.2rem; }
  .gigi-avatar img { width:100%;height:100%;object-fit:contain;padding:4px; }
  .gigi-name { font-weight:800;font-size:.88rem; }
  .gigi-clinic { font-size:.68rem;opacity:.8; }
  .gigi-tools { margin-left:auto;display:flex;gap:4px; }
  .gigi-tool { width:30px;height:30px;border:0;border-radius:8px;background:rgba(255,255,255,.15);
    cursor:pointer;font-size:.85rem;display:flex;align-items:center;justify-content:center; }
  .gigi-tool.on, .gigi-tool.mic.on { background:rgba(239,68,68,.6);animation:pulse 1.2s ease infinite; }
  @keyframes pulse { 0%,100%{opacity:1}50%{opacity:.6} }
  .gigi-msgs { flex:1;overflow-y:auto;padding:14px;display:flex;flex-direction:column;gap:8px;
    background:rgba(248,250,252,.5); }
  .gigi-msg { display:flex;flex-direction:column;max-width:85%;animation:slideUp .25s ease; }
  .gigi-msg.bot { align-self:flex-start; }
  .gigi-msg.user { align-self:flex-end; }
  @keyframes slideUp { from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)} }
  .gigi-bubble { padding:10px 14px;border-radius:16px;font-size:.85rem;line-height:1.45;word-break:break-word; }
  .gigi-msg.bot .gigi-bubble { background:#fff;border:1px solid #e5e7eb;border-bottom-left-radius:4px;color:#1f2937; }
  .gigi-msg.user .gigi-bubble { background:var(--gigi);color:#fff;border-bottom-right-radius:4px; }
  .gigi-time { font-size:.58rem;color:#9ca3af;margin-top:2px;padding:0 4px; }
  .gigi-msg.user .gigi-time { text-align:right; }
  .thinking { display:flex;gap:4px;align-items:center;padding:12px 16px!important; }
  .dot { width:6px;height:6px;border-radius:50%;background:#9ca3af;animation:bounce 1.4s ease infinite; }
  .dot:nth-child(2){animation-delay:.2s}.dot:nth-child(3){animation-delay:.4s}
  @keyframes bounce { 0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)} }
  .gigi-card { background:linear-gradient(135deg,#ecfdf5,#d1fae5);border:1px solid #a7f3d0;
    border-radius:16px;padding:16px;margin:4px 0;animation:slideUp .3s ease; }
  .gigi-card :global(b){display:block;font-size:.9rem;margin-bottom:2px;}
  .gigi-card :global(small){display:block;color:#6b7280;margin-top:6px;}
  .gigi-card-actions { display:flex;justify-content:center;margin-top:10px; }
  .gigi-card-actions button { padding:8px 20px;border:0;border-radius:10px;
    background:var(--gigi);color:#fff;font-weight:700;cursor:pointer;font:inherit;font-size:.82rem; }
  .gigi-chips { display:flex;flex-wrap:wrap;gap:6px;padding:8px 14px;border-top:1px solid rgba(0,0,0,.04); }
  .gigi-chips button { padding:6px 12px;border-radius:999px;border:1px solid color-mix(in srgb,var(--gigi) 25%,transparent);
    background:rgba(255,255,255,.7);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);
    color:var(--gigi);font:inherit;font-size:.72rem;font-weight:600;cursor:pointer; }
  .gigi-chips button:hover { background:var(--gigi);color:#fff; }
  .gigi-input-bar { display:flex;gap:8px;padding:12px 14px;border-top:1px solid rgba(0,0,0,.05);background:rgba(255,255,255,.5); }
  .gigi-input-bar input { flex:1;border:1px solid #e5e7eb;border-radius:12px;padding:10px 14px;font:inherit;font-size:.85rem;background:#fff; }
  .gigi-input-bar input:focus { outline:none;border-color:var(--gigi);box-shadow:0 0 0 3px color-mix(in srgb,var(--gigi) 15%,transparent); }
  .gigi-send { width:38px;height:38px;border:0;border-radius:12px;background:var(--gigi);color:#fff;
    cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0; }
  .gigi-send:disabled { opacity:.4;cursor:not-allowed; }
  .gigi-input-bar.done { justify-content:center; }
  .gigi-input-bar.done button { padding:10px 24px;border:0;border-radius:12px;background:var(--gigi);color:#fff;
    font:inherit;font-weight:700;cursor:pointer;font-size:.85rem; }
  .gigi-error { color:#dc2626;font-size:.72rem;padding:0 14px 8px;text-align:center; }
</style>
